import { WORLD_NODES, isSlotNodeType, type RealmView } from '@goodtime/shared';
import { themeIdForNode } from '../../themes/slotTheme';

const KEY = 'goodtime.overviewStrip';

function remembered(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function remember(on: boolean): void {
  try {
    if (on === remembered()) return;
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    // Storage off (private mode): the flag still applies once loaded.
  }
}

/**
 * Whether the overview is the traveller's map strip (flag overview_strip, with realm_map and world_maps on). Like realmHome, the
 * flag arrives with the player fetch a moment after launch; until then the last answer stands, so the old overview never flashes
 * before the strip (and the strip never before the old one). A plain function rather than a hook, for App's sake.
 */
export function overviewHome(flagOn: boolean, playerLoaded: boolean): boolean {
  if (!playerLoaded) return flagOn || remembered();
  remember(flagOn);
  return flagOn;
}

const LAMPS_KEY = 'goodtime.overviewLamps';

/** The lit worlds (indexes into BIOMES) this device remembers, or none. */
export function loadLitWorlds(): number[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LAMPS_KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((n): n is number => Number.isInteger(n) && n >= 0 && n < 64) : [];
  } catch {
    return [];
  }
}

export function saveLitWorlds(worlds: readonly number[]): void {
  try {
    localStorage.setItem(LAMPS_KEY, JSON.stringify([...new Set(worlds)].sort((a, b) => a - b)));
  } catch {
    // Storage off: the lamps light again next launch.
  }
}

let themeWorlds: Map<string, number> | null = null;

/** The world (index into BIOMES) a slot's theme id belongs to, or -1. */
export function worldOfTheme(themeId: string): number {
  if (!themeWorlds) {
    themeWorlds = new Map();
    for (const n of WORLD_NODES) {
      if (isSlotNodeType(n.type)) themeWorlds.set(themeIdForNode(n), n.biome);
    }
  }
  return themeWorlds.get(themeId) ?? -1;
}

/** The worlds a realm view says the player has played a slot in. */
export function worldsPlayed(view: RealmView): number[] {
  const worlds = new Set<number>();
  for (const s of view.slots ?? []) {
    const w = worldOfTheme(s.themeId);
    if (w >= 0) worlds.add(w);
  }
  return [...worlds].sort((a, b) => a - b);
}
